import type { OracleFeedHealth } from '../perps/oracle-health'
import { JSONContent } from '@tiptap/core'
import { Answer, MAX_ANSWERS } from 'common/answer'
import { getAnswerProbability, getProbability } from 'common/calculate'
import {
  Contract,
  isMultiCpmm,
  MAX_QUESTION_LENGTH,
  MultiContract,
  RESOLUTIONS,
  VISIBILITIES,
} from 'common/contract'
import { MINIMUM_BOUNTY } from 'common/economy'
import { DOMAIN } from 'common/envs/constants'
import { MAX_ID_LENGTH } from 'common/group'
import { MARKET_LINK_RELATIONS } from 'common/market-links'
import { MAX_MULTI_NUMERIC_ANSWERS } from 'common/multi-numeric'
import { MAX_ANSWER_PROB, MIN_ANSWER_PROB } from 'common/new-contract'
import { MIN_PERP_LEVERAGE, PERP_MIN_CLOSE_FRACTION } from 'common/perps/amm'
import { PERP_CREATOR_ACCOUNTS } from 'common/perps/creator-accounts'
import {
  PERP_TICKER_MAX_LENGTH,
  PERP_TICKER_PATTERN,
  getPerpTicker,
} from 'common/perps/ticker'
import {
  getPerpEffectiveTakerFeeBps,
  getPerpTakerFeeBps,
  getPerpTakerFeeImpact,
  PERP_TAKER_FEE_API_BPS_MAX,
  PERP_TAKER_FEE_IMPACT_MAX,
} from 'common/perps/fees'
import { getMappedValue } from 'common/pseudo-numeric'
import {
  getTierIndexFromLiquidityAndAnswers,
  liquidityTiers,
} from 'common/tier'
import { removeUndefinedProps } from 'common/util/object'
import { richTextToString } from 'common/util/parse'
import { randomStringRegex } from 'common/util/random'
import { z } from 'zod'
import { coerceBoolean, contentSchema } from './zod-types'

export type LiteMarket = {
  // Unique identifier for this market
  id: string

  // Attributes about the creator
  creatorId: string
  creatorUsername: string
  creatorName: string
  createdTime: number
  creatorAvatarUrl?: string

  // Market attributes. All times are in milliseconds since epoch
  closeTime?: number
  question: string
  slug: string
  url: string
  outcomeType: string
  mechanism: string

  pool?: { [outcome: string]: number }
  probability?: number
  p?: number
  totalLiquidity?: number
  answers?: {
    id: string
    text: string
    probability: number
  }[]
  // For pseudo-numeric
  value?: number
  min?: number
  max?: number

  volume: number
  volume24Hours: number

  isResolved: boolean
  resolution?: string
  resolutionTime?: number
  resolutionProbability?: number
  resolverId?: string

  uniqueBettorCount: number
  lastUpdatedTime?: number
  lastBetTime?: number
  sportsStartTimestamp?: string
  sportsEventId?: string
  sportsLeague?: string

  // Perp markets only (mechanism 'perp'). Exposed so clients (and the perp
  // market page's live poll) can track price/pools without bespoke endpoints.
  oracleFeedId?: string
  oraclePrice?: number
  oraclePriceTime?: number
  oracleSourceTime?: number | null
  oracleFeedHealth?: OracleFeedHealth
  poolLong?: number
  poolShort?: number
  // Drives the live funding rate (getPerpFundingRate) — must travel with the
  // pools so a polling client's rate stays in step with the engine's.
  openInterestLong?: number
  openInterestShort?: number
  fundingRate?: number
  lastFundingTime?: number
  maxLeverage?: number
  takerFeeBps?: number
  // Size-impact coefficient of the taker fee. Travels with the pools so a
  // client can price its own fee: the marginal rate is
  // takerFeeBps + takerFeeImpact·(share of pool)² bps (see calcPerpSizeFee).
  takerFeeImpact?: number
  // What an API-key open is charged, in bps of notional. Equal to
  // takerFeeBps unless this market prices the API channel separately. The
  // channel that pays this rate is the one that reads the market over the
  // API, so publishing only takerFeeBps would tell every bot the wrong
  // number — and it is the input a client needs to size a trade.
  takerFeeApiBps?: number
  resolvedOraclePrice?: number
  // The market's short on-site identifier ("BTC", "TRUMP"): the label the
  // /perps hub and the title badge show, and what search matches on.
  ticker?: string
}
export type ApiAnswer = Omit<
  Answer & {
    probability: number
    pool: { YES: number; NO: number }
  },
  'prob' | 'poolYes' | 'poolNo'
>
export type FullMarket = LiteMarket & {
  // bets?: Bet[]
  // comments?: Comment[]

  // multi markets only
  answers?: ApiAnswer[]
  shouldAnswersSumToOne?: boolean
  addAnswersMode?: 'ANYONE' | 'ONLY_CREATOR' | 'DISABLED'

  // poll only
  options?: { text: string; votes: number }[]

  // bounty only
  totalBounty?: number
  bountyLeft?: number

  description: string | JSONContent
  textDescription: string // string version of description
  coverImageUrl?: string
  groupSlugs?: string[]
}

export function toLiteMarket(
  contract: Contract,
  props: { includeLiteAnswers?: boolean } = { includeLiteAnswers: false }
): LiteMarket {
  const {
    id,
    creatorId,
    creatorUsername,
    creatorName,
    createdTime,
    creatorAvatarUrl,
    closeTime,
    question,
    slug,
    outcomeType,
    mechanism,
    volume,
    volume24Hours,
    isResolved,
    resolution,
    resolutionTime,
    resolutionProbability,
    resolverId,
    uniqueBettorCount,
    lastUpdatedTime,
    lastBetTime,
    lastCommentTime,
    loverUserId1,
    loverUserId2,
    matchCreatorId,
    isLove,
    token,
    siblingContractId,
  } = contract
  const { includeLiteAnswers } = props

  const { p, totalLiquidity } = contract as any
  const { sportsStartTimestamp, sportsEventId, sportsLeague } = contract as any

  const probability =
    outcomeType === 'BINARY' || outcomeType === 'PSEUDO_NUMERIC'
      ? getProbability(contract)
      : undefined

  let numericValues = {}
  if (contract.outcomeType === 'PSEUDO_NUMERIC') {
    const value = getMappedValue(contract, contract.prob)
    const { min, max, isLogScale } = contract
    numericValues = { value, min, max, isLogScale }
  }
  const answers =
    includeLiteAnswers && isMultiCpmm(contract)
      ? contract.answers?.map((answer) => ({
          id: answer.id,
          text: answer.text,
          probability: getAnswerProbability(contract, answer.id),
        }))
      : undefined

  return removeUndefinedProps({
    id,
    creatorId,
    creatorUsername,
    creatorName,
    createdTime,
    creatorAvatarUrl,
    closeTime:
      resolutionTime && closeTime
        ? Math.min(resolutionTime, closeTime)
        : closeTime,
    question,
    slug,
    url: `https://${DOMAIN}/${creatorUsername}/${slug}`,
    pool: 'pool' in contract ? contract.pool : undefined,
    probability,
    p,
    totalLiquidity,
    outcomeType,
    mechanism,
    volume,
    volume24Hours,
    isResolved,
    resolution,
    resolutionTime,
    resolutionProbability,
    resolverId,
    uniqueBettorCount,
    lastUpdatedTime,
    lastBetTime,
    lastCommentTime,
    ...numericValues,
    token,
    siblingContractId,
    answers,

    // Sports market props (only present on sports markets).
    sportsStartTimestamp,
    sportsEventId,
    sportsLeague,

    // Perp props (only present on perp markets).
    ...(contract.mechanism === 'perp'
      ? {
          oracleFeedId: contract.oracleFeedId,
          oraclePrice: contract.oraclePrice,
          oraclePriceTime: contract.oraclePriceTime,
          oracleSourceTime: contract.oracleSourceTime,
          oracleFeedHealth: contract.oracleFeedHealth,
          poolLong: contract.poolLong,
          poolShort: contract.poolShort,
          openInterestLong: contract.openInterestLong,
          openInterestShort: contract.openInterestShort,
          fundingRate: contract.fundingRate,
          lastFundingTime: contract.lastFundingTime,
          maxLeverage: contract.maxLeverage,
          takerFeeBps: getPerpTakerFeeBps(contract),
          takerFeeImpact: getPerpTakerFeeImpact(contract),
          takerFeeApiBps: getPerpEffectiveTakerFeeBps(contract, true),
          resolvedOraclePrice: contract.resolvedOraclePrice,
          ticker: getPerpTicker(contract),
        }
      : {}),

    // Manifold love props.
    loverUserId1,
    loverUserId2,
    matchCreatorId,
    isLove,
  })
}

export function toFullMarket(contract: Contract): FullMarket {
  const liteMarket = toLiteMarket(contract)
  const { outcomeType } = contract
  const answers = isMultiCpmm(contract)
    ? contract.answers.map((answer) =>
        augmentAnswerWithProbability(contract, answer)
      )
    : undefined

  let multiValues = {}
  if (isMultiCpmm(contract)) {
    multiValues = {
      shouldAnswersSumToOne: contract.shouldAnswersSumToOne,
      addAnswersMode: contract.addAnswersMode,
    }
  } else if (outcomeType === 'MULTIPLE_CHOICE') {
    multiValues = {
      shouldAnswersSumToOne: true,
      addAnswersMode: 'DISABLED',
    }
  }
  const options =
    outcomeType === 'POLL'
      ? contract.options.map(({ text, votes }) => ({ text, votes }))
      : undefined

  const bountyValues =
    outcomeType === 'BOUNTIED_QUESTION'
      ? {
          totalBounty: contract.totalBounty,
          bountyLeft: contract.bountyLeft,
        }
      : {}

  const { description, coverImageUrl, groupSlugs } = contract

  return {
    ...liteMarket,
    ...bountyValues,
    ...multiValues,
    answers,
    options,
    description,
    coverImageUrl,
    groupSlugs,
    textDescription:
      typeof description === 'string'
        ? description
        : richTextToString(description),
  }
}

export type UltraLiteMarket = {
  // Unique identifier for this market
  id: string

  // Attributes about the creator
  creatorId: string
  creatorUsername: string
  creatorName: string
  createdTime: string

  question: string
  url: string
  outcomeType: string

  probability?: number
  liquidityTier?: string
  // Perp markets only. Omitted when the source value is not finite.
  oracleFeedId?: string
  oraclePrice?: number
  // Current perp backing capital (poolLong + poolShort).
  backingPool?: number
  answers?: {
    id: string
    text: string
    probability: number
  }[]
  // For pseudo-numeric
  value?: number

  volume: number
  volume24Hours: number

  isResolved: boolean
  resolution?: string
  resolutionTime?: string

  uniqueBettorCount: number
}
export function toUltraLiteMarket(liteMarket: LiteMarket): UltraLiteMarket {
  const {
    id,
    creatorId,
    creatorName,
    creatorUsername,
    answers,
    question,
    probability,
    totalLiquidity,
    outcomeType,
    volume,
    volume24Hours,
    isResolved,
    resolution,
    resolutionTime,
    uniqueBettorCount,
    url,
    createdTime,
  } = liteMarket
  const isPerp = outcomeType === 'PERP'
  const tier =
    !isPerp && totalLiquidity
      ? getTierIndexFromLiquidityAndAnswers(
          totalLiquidity,
          answers?.length ?? 0
        )
      : undefined
  let liquidityTier = 'n/a'
  if (tier !== undefined) {
    if (tier === 0) {
      liquidityTier = 'low'
    } else if (tier === 1) {
      liquidityTier = 'medium'
    } else if (tier === 2) {
      liquidityTier = 'high'
    } else if (tier > 2) {
      liquidityTier = 'very high'
    }
  }
  const oraclePrice =
    isPerp && isFiniteNumber(liteMarket.oraclePrice)
      ? liteMarket.oraclePrice
      : undefined
  const rawBackingPool =
    isPerp &&
    isFiniteNonNegativeNumber(liteMarket.poolLong) &&
    isFiniteNonNegativeNumber(liteMarket.poolShort)
      ? liteMarket.poolLong + liteMarket.poolShort
      : undefined
  const backingPool = isFiniteNumber(rawBackingPool)
    ? rawBackingPool
    : undefined

  return {
    id,
    url,
    creatorId,
    creatorName,
    creatorUsername,
    answers,
    question,
    probability,
    ...(isPerp
      ? {
          ...(liteMarket.oracleFeedId
            ? { oracleFeedId: liteMarket.oracleFeedId }
            : {}),
          ...(oraclePrice === undefined ? {} : { oraclePrice }),
          ...(backingPool === undefined ? {} : { backingPool }),
        }
      : { liquidityTier }),
    outcomeType,
    volume: Math.round(volume),
    volume24Hours: Math.round(volume24Hours),
    isResolved,
    resolution,
    resolutionTime: resolutionTime
      ? new Date(resolutionTime).toISOString()
      : undefined,
    createdTime: new Date(createdTime).toISOString(),
    uniqueBettorCount,
  }
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isFiniteNonNegativeNumber = (value: unknown): value is number =>
  isFiniteNumber(value) && value >= 0

function augmentAnswerWithProbability(
  contract: MultiContract,
  answer: Answer
): ApiAnswer {
  const probability = getAnswerProbability(contract, answer.id)
  const { poolYes, poolNo, prob: _, ...other } = answer
  const pool = {
    YES: poolYes,
    NO: poolNo,
  }
  return {
    ...other,
    pool,
    probability,
  }
}

// ZOD TYPES

// create market

export const createBinarySchema = z.object({
  outcomeType: z.enum(['BINARY', 'STONK']),
  initialProb: z.number().min(1).max(99).optional(),
})

export const createNumericSchema = z.object({
  outcomeType: z.enum(['PSEUDO_NUMERIC']),
  min: z.number().safe(),
  max: z.number().safe(),
  initialValue: z.number().safe(),
  isLogScale: z.boolean().optional(),
})

export const createMultiSchema = z.object({
  outcomeType: z.enum(['MULTIPLE_CHOICE']),
  answers: z.array(z.string().trim().min(1)).max(MAX_ANSWERS),
  answerShortTexts: z
    .array(z.string().trim().min(1))
    .max(MAX_ANSWERS)
    .optional(),
  answerImageUrls: z
    .array(z.string().trim().min(1))
    .max(MAX_ANSWERS)
    .optional(),
  addAnswersMode: z
    .enum(['DISABLED', 'ONLY_CREATOR', 'ANYONE'])
    .default('DISABLED'),
  shouldAnswersSumToOne: z.boolean().optional(),
  // Starting probability of each answer, in percent, in the same order as
  // `answers`. Defaults to an even split. For answers that sum to one these
  // must add up to 100 — or to less than 100 when the market has an 'Other'
  // answer, which takes the remainder.
  answerProbs: z
    .array(z.number().gte(MIN_ANSWER_PROB).lte(MAX_ANSWER_PROB))
    .max(MAX_ANSWERS)
    .optional(),
})

export const createNumberSchema = z.object({
  outcomeType: z.enum(['NUMBER']),
  min: z.number().safe(),
  max: z.number().safe(),
  precision: z.number().gt(0),
})

export const createMultiNumericSchema = z.object({
  outcomeType: z.enum(['MULTI_NUMERIC']),
  answers: z.array(z.string().trim().min(1)).max(MAX_MULTI_NUMERIC_ANSWERS),
  midpoints: z.array(z.number().safe()).max(MAX_MULTI_NUMERIC_ANSWERS),
  shouldAnswersSumToOne: z.boolean(),
  addAnswersMode: z.enum(['DISABLED']).default('DISABLED'),
  unit: z.string(),
})

export const createMultiDateSchema = z.object({
  outcomeType: z.enum(['DATE']),
  answers: z.array(z.string().trim().min(1)).max(MAX_MULTI_NUMERIC_ANSWERS),
  midpoints: z.array(z.number().safe()).max(MAX_MULTI_NUMERIC_ANSWERS),
  shouldAnswersSumToOne: z.boolean(),
  addAnswersMode: z.enum(['DISABLED']).default('DISABLED'),
  timezone: z.string(),
})

export const createBountySchema = z.object({
  outcomeType: z.enum(['BOUNTIED_QUESTION']),
  totalBounty: z.number().min(MINIMUM_BOUNTY),
  isAutoBounty: z.boolean().optional(),
})

export const POLL_TYPES = ['single', 'multi-select', 'ranked-choice'] as const

export const createPollSchema = z.object({
  outcomeType: z.enum(['POLL']),
  answers: z.array(z.string().trim().min(1)).min(2).max(MAX_ANSWERS),
  voterVisibility: z.enum(['creator', 'everyone']).optional(),
  pollType: z.enum(POLL_TYPES).default('single').optional(),
  // For multi-select: max number of options user can select (defaults to all if not specified)
  maxSelections: z.number().min(1).optional(),
})

export const createMarketProps = z
  .object({
    question: z.string().min(1).max(MAX_QUESTION_LENGTH),
    description: contentSchema.or(z.string()).optional(),
    descriptionHtml: z.string().optional(),
    descriptionMarkdown: z.string().optional(),
    descriptionJson: z.string().optional(),
    closeTime: z
      .union([z.date(), z.number()])
      .refine(
        (date) =>
          (typeof date === 'number' ? date : date.getTime()) > Date.now(),
        'Close time must be in the future.'
      )
      .optional(),
    // Explicit POLL "Never" overrides a linked parent's deadline. Omission
    // keeps the existing close-time default/inheritance behavior.
    neverCloses: z.boolean().optional(),
    groupIds: z.array(z.string().min(1).max(MAX_ID_LENGTH)).optional(),
    visibility: z.enum(VISIBILITIES).default('public').optional(),
    isTwitchContract: z.boolean().optional(),
    utcOffset: z.number().optional(),
    extraLiquidity: z.number().min(1).optional(),
    liquidityTier: z.number().min(liquidityTiers[0]),
    idempotencyKey: z.string().regex(randomStringRegex).length(10).optional(),
    sportsStartTimestamp: z.string().optional(),
    sportsEventId: z.string().optional(),
    sportsLeague: z.string().optional(),
    takerAPIOrdersDisabled: coerceBoolean.optional(),
    // The market this one is about (see common/market-links). Without an
    // explicit close time, ordinary markets and polls inherit its deadline.
    // Polls can request neverCloses; STONKs/bounties keep native defaults.
    linkedToContractId: z.string().min(1).max(MAX_ID_LENGTH).optional(),
    linkRelation: z.enum(MARKET_LINK_RELATIONS).optional(),
  })
  .and(
    z.union([
      createMultiSchema,
      createNumericSchema,
      createBountySchema,
      createPollSchema,
      createBinarySchema,
      createNumberSchema,
      createMultiNumericSchema,
      createMultiDateSchema,
    ])
  )
  .superRefine(({ neverCloses, outcomeType, closeTime }, ctx) => {
    if (!neverCloses) return
    if (outcomeType !== 'POLL')
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only polls can explicitly request neverCloses.',
        path: ['neverCloses'],
      })
    if (closeTime !== undefined)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose either neverCloses or closeTime, not both.',
        path: ['closeTime'],
      })
  })

export const updateMarketProps = z
  .object({
    contractId: z.string(),
    question: z.string().min(1).max(MAX_QUESTION_LENGTH).optional(),
    visibility: z.enum(['unlisted', 'public']).optional(),
    closeTime: z.number().optional(),
    addAnswersMode: z.enum(['ONLY_CREATOR', 'ANYONE']).optional(),
    coverImageUrl: z.string().or(z.null()).optional(),
    sort: z.string().optional(),
    description: z.string().optional(),
    descriptionHtml: z.string().optional(),
    descriptionMarkdown: z.string().optional(),
    descriptionJson: z.string().optional(),
    display: z.enum(['clock', 'default']).optional(),
    homePageScoreAdjustment: z.number().gte(-1).lte(1).nullable().optional(),
    homePageScoreAdjustmentDays: z.number().int().positive().optional(),
    creatorBannedFromBetting: z.boolean().optional(),
    // Perp markets only, admin only; a feed named in PERP_FEED_TICKERS
    // accepts only its canonical ticker (see update-market).
    ticker: z
      .string()
      .max(PERP_TICKER_MAX_LENGTH)
      .regex(
        PERP_TICKER_PATTERN,
        'A ticker is one alphanumeric token that starts with a letter'
      )
      .optional(),
  })
  .strict()

// resolve market

export const resolveBinarySchema = z
  .object({
    outcome: z.enum(RESOLUTIONS),
    probabilityInt: z.number().gte(0).lte(100).optional(),

    // To resolve one answer of multiple choice. Only independent answers supported (shouldAnswersSumToOne = false)
    answerId: z.string().optional(),
  })
  .passthrough() // overlaps with pseudo-numeric

// For multiple choice with shouldAnswersSumToOne = true
export const resolveMultiSchema = z.union([
  z.object({
    outcome: z.literal('CANCEL'),
  }),
  z.object({
    outcome: z.literal('CHOOSE_ONE'),
    answerId: z.string(),
  }),
  z.object({
    outcome: z.literal('CHOOSE_MULTIPLE'),
    resolutions: z.array(
      z.object({
        answerId: z.string(),
        pct: z.number().gte(0).lte(100),
      })
    ),
  }),
])

export const resolvePseudoNumericSchema = z.union([
  z.object({
    outcome: z.literal('CANCEL'),
  }),
  z.object({
    outcome: z.literal('MKT'),
    value: z.number(),
    probabilityInt: z.number().gte(0).lte(100),
  }),
])

export const resolveMarketProps = z
  .object({
    contractId: z.string(),
  })
  .and(
    z.union([
      resolveBinarySchema,
      resolveMultiSchema,
      resolvePseudoNumericSchema,
    ])
  )

// ---- Perpetual futures (ManiPerp) ----

export const createPerpSchema = z.object({
  question: z.string().min(1).max(MAX_QUESTION_LENGTH),
  description: contentSchema.or(z.string()).optional(),
  descriptionHtml: z.string().optional(),
  descriptionMarkdown: z.string().optional(),
  descriptionJson: z.string().optional(),
  // No .default() here: `.optional()` short-circuits undefined before a
  // default is applied, so a default on this chain never runs and only
  // misleads. The handler owns the default, and it is 'unlisted'.
  visibility: z.enum(VISIBILITIES).optional(),
  groupIds: z.array(z.string().min(1).max(MAX_ID_LENGTH)).optional(),
  oracleFeedId: z.string().min(1).max(200),
  // Short on-site identifier shown in place of the market type ("BTC").
  // Omitted = the feed's canonical ticker (PERP_FEED_TICKERS), else one
  // derived from the feed id; a feed that has a canonical ticker rejects any
  // other value, so the label can't drift between markets on one feed.
  ticker: z
    .string()
    .max(PERP_TICKER_MAX_LENGTH)
    .regex(
      PERP_TICKER_PATTERN,
      'A ticker is one alphanumeric token that starts with a letter'
    )
    .optional(),
  maxLeverage: z.number().gt(1).lte(100),
  maxFundingRate: z.number().gt(0).lt(1),
  fundingSensitivity: z.number().gt(0).lte(100),
  maxOraclePriceAgeMs: z.number().int().positive(),
  subsidyLong: z.number().gt(0),
  subsidyShort: z.number().gt(0),
  // Open-side taker fee in bps of notional (closing is free). Omitted = the
  // feed default (MNX_DEFAULT_FEES for MNX, PERP_TAKER_FEE_BPS_DEFAULT
  // otherwise); the handler stamps
  // the resolved value so later default changes cannot rewrite an existing
  // market's economics.
  takerFeeBps: z.number().min(0).max(100).optional(),
  // API-key opens use max(web base, API base). MNX defaults to 20 bps;
  // other feeds inherit the web base when this is omitted.
  takerFeeApiBps: z.number().min(0).max(PERP_TAKER_FEE_API_BPS_MAX).optional(),
  // Size-impact coefficient of the taker fee (marginal rate is
  // takerFeeBps + takerFeeImpact·(share of pool)² bps). Omitted = the
  // feed default (10 on MNX, PERP_TAKER_FEE_IMPACT_DEFAULT otherwise); stamped like
  // takerFeeBps above.
  takerFeeImpact: z.number().min(0).max(PERP_TAKER_FEE_IMPACT_MAX).optional(),
  // Which account owns the market. The owner pays the backing now and is paid
  // the residual pool at settlement, so the caller must be the official
  // Manifold account either way (it spends its own balance or acts for the
  // partner). Omitted = 'manifold' (the handler owns the default, as with
  // visibility); a partner is accepted only on its own feeds.
  creatorAccount: z.enum(PERP_CREATOR_ACCOUNTS).optional(),
})

export const placePerpTradeSchema = z.object({
  contractId: z.string().min(1),
  direction: z.enum(['long', 'short']),
  mana: z.number().gt(0),
  leverage: z.number().min(MIN_PERP_LEVERAGE),
  idempotencyKey: z.string().regex(randomStringRegex).length(10),
  // Price protection: reject rather than charge when the fee computed at
  // execution exceeds this (mana). The fee is state-dependent — pools move
  // and config is live-tunable — so the previewed fee is not a promise;
  // this bound is how a caller makes their consent explicit. Optional here
  // for flat-fee markets, but the ENGINE requires it whenever the market's
  // takerFeeImpact is nonzero (mirroring the close path's mandatory
  // expectedOpenedTime); the web panel always sends it.
  maxFee: z.number().min(0).optional(),
})

export const closePerpPositionSchema = z
  .object({
    contractId: z.string().min(1),
    direction: z.enum(['long', 'short']),
    idempotencyKey: z.string().regex(randomStringRegex).length(10),
    expectedOpenedTime: z.number().int().nonnegative(),
    // Fraction of the position to close; omitted = the whole position. Either
    // exactly 1 or at least PERP_MIN_CLOSE_FRACTION — a smaller close cannot
    // change the position and would only mint an event and streak credit. A
    // fraction whose remainder would be dust closes the whole position instead;
    // the response's `fraction` says what actually happened.
    fraction: z
      .number()
      .lte(1)
      .refine((f) => f === 1 || f >= PERP_MIN_CLOSE_FRACTION, {
        message: `fraction must be 1 or at least ${PERP_MIN_CLOSE_FRACTION}`,
      })
      .optional(),
    // The notional the caller sized `fraction` against. Checked against the
    // locked row on a partial close and rejected with a 409 if it has moved,
    // because `expectedOpenedTime` cannot see an intervening partial close: the
    // survivor keeps its openedTime. Ignored on a full close, which is
    // unambiguous at any size.
    expectedSize: z.number().positive().finite().optional(),
  })
  // Required, not optional, for a partial close: without it the race is
  // simply unguarded, and "close half of whatever happens to be there" is
  // never what a caller means when they previewed a number. A caller has to
  // read the position to know it exists, so it always has a size to send; if
  // it raced, the 409 tells it to re-read rather than executing a trade it
  // did not preview.
  .refine(
    (p) =>
      p.fraction === undefined ||
      p.fraction >= 1 ||
      p.expectedSize !== undefined,
    {
      message: 'expectedSize is required when closing part of a position',
      path: ['expectedSize'],
    }
  )
